import { useState } from 'react'
import { useBreadcrumb } from '../hooks/useBreadcrumb'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { FaBell, FaBullhorn, FaTruck, FaClipboardCheck, FaExclamationTriangle, FaCheckDouble, FaEnvelope, FaEnvelopeOpen } from 'react-icons/fa'
import { Card, Button, PageHeader, LoadingSpinner } from '../components/common'
import { get, post } from '../services/api'
import { useAuth } from '../hooks/useAuth'
import { useGoLogin } from '../hooks/useGoLogin'
import { formatDateTime, fromNow } from '../utils/datetime'

const notificationTypes = [
  { key: 'all', label: '全部', icon: FaBell },
  { key: 'announcement', label: '系统公告', icon: FaBullhorn },
  { key: 'logistics', label: '物流提醒', icon: FaTruck },
  { key: 'inspection', label: '验货通知', icon: FaClipboardCheck },
  { key: 'alert', label: '异常预警', icon: FaExclamationTriangle },
]

const typeConfig = {
  announcement: { icon: FaBullhorn, color: 'bg-blue-100 text-blue-600', label: '系统公告' },
  logistics: { icon: FaTruck, color: 'bg-green-100 text-green-600', label: '物流提醒' },
  inspection: { icon: FaClipboardCheck, color: 'bg-purple-100 text-purple-600', label: '验货通知' },
  alert: { icon: FaExclamationTriangle, color: 'bg-red-100 text-red-600', label: '异常预警' },
}

export default function NotificationsPage() {
  const breadcrumb = useBreadcrumb()
  const { isAuthenticated } = useAuth()
  const goLogin = useGoLogin()
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState('all')
  const [selectedNotification, setSelectedNotification] = useState(null)

  // 获取通知列表
  const { data: notificationsData, isLoading } = useQuery({
    queryKey: ['notifications', activeTab],
    queryFn: () => get('/blade-portal/notification/list', { 
      type: activeTab === 'all' ? undefined : activeTab,
      pageSize: 50
    }),
    enabled: isAuthenticated,
  })

  // 获取未读数量
  const { data: unreadCountData } = useQuery({
    queryKey: ['notifications-unread-count'],
    queryFn: () => get('/blade-portal/notification/unread-count'),
    enabled: isAuthenticated,
  })

  // 获取系统公告（无需登录）
  const { data: announcementsData, isLoading: announcementsLoading } = useQuery({
    queryKey: ['announcements'],
    queryFn: () => get('/blade-portal/notification/announcements'),
    enabled: !isAuthenticated,
  })

  // 标记已读
  const markReadMutation = useMutation({
    mutationFn: (id) => post('/blade-portal/notification/mark-read', { id }),
    onSuccess: () => {
      queryClient.invalidateQueries(['notifications'])
      queryClient.invalidateQueries(['notifications-unread-count'])
    }
  })

  // 全部标记已读
  const markAllReadMutation = useMutation({
    mutationFn: () => post('/blade-portal/notification/mark-all-read'),
    onSuccess: () => {
      toast.success('已全部标记为已读')
      queryClient.invalidateQueries(['notifications'])
      queryClient.invalidateQueries(['notifications-unread-count'])
    }
  })

  const notifications = notificationsData?.records || []
  const announcements = announcementsData?.records || []
  const unreadCount = unreadCountData?.count || 0

  // 点击通知详情
  const handleNotificationClick = (notification) => {
    setSelectedNotification(notification)
    if (!notification.is_read) {
      markReadMutation.mutate(notification.id)
    }
  }

  // 未登录时显示系统公告
  if (!isAuthenticated) {
    return (
      <div>
        <PageHeader
          icon={FaBullhorn}
          title="系统公告"
          subtitle="查看最新系统公告"
          // 显式覆盖：这是同一条路由在未登录时的另一副面孔，不是「消息中心」的下级，
          // 站点结构里无从表达，故不走 useBreadcrumb
          breadcrumb={[{ label: '系统公告' }]}
        />

        <div className="container py-8">
          {announcementsLoading ? (
            <div className="text-center py-20">
              <LoadingSpinner size="lg" />
            </div>
          ) : announcements.length === 0 ? (
            <Card>
              <Card.Body>
                <div className="text-center py-12">
                  <FaBullhorn className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">暂无系统公告</p>
                </div>
              </Card.Body>
            </Card>
          ) : (
            <div className="space-y-4">
              {announcements.map((item) => (
                <Card key={item.id} className="hover:shadow-md transition-shadow">
                  <Card.Body>
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <FaBullhorn className="w-6 h-6 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-lg mb-2">{item.title}</h4>
                        <p className="text-gray-600 mb-3">{item.content}</p>
                        <p className="text-sm text-gray-400">
                          {formatDateTime(item.create_time, 'YYYY-MM-DD HH:mm')}
                        </p>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              ))}
            </div>
          )}

          <div className="mt-8 text-center">
            <p className="text-gray-500 mb-4">登录后查看更多通知</p>
            {/* Button 渲染的是 <button>、不认 href —— 原先写 href="/login" 点了完全没反应。
                见 src/test/login-return-to.test.jsx */}
            <Button onClick={goLogin}>立即登录</Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div>
      <PageHeader
        icon={FaBell}
        title="消息中心"
        subtitle="查看系统公告、物流提醒、验货通知等消息"
        breadcrumb={breadcrumb}
        action={
          unreadCount > 0 && (
            <Button 
              variant="outline" 
              size="sm" 
              icon={<FaCheckDouble />}
              onClick={() => markAllReadMutation.mutate()}
              loading={markAllReadMutation.isPending}
            >
              全部已读
            </Button>
          )
        }
      />

      <div className="container py-8">
        <div className="grid lg:grid-cols-4 gap-6">
          {/* 左侧分类 */}
          <div className="lg:col-span-1">
            <Card>
              <Card.Body className="p-2">
                <nav className="space-y-1">
                  {notificationTypes.map((type) => {
                    const Icon = type.icon
                    return (
                      <button
                        key={type.key}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                          activeTab === type.key
                            ? 'bg-primary text-white'
                            : 'hover:bg-gray-100 text-gray-700'
                        }`}
                        onClick={() => setActiveTab(type.key)}
                        style={{
                          backgroundColor: activeTab === type.key ? '#1D4ED8' : '#F3F4F6', // Ensure visible background color
                          color: activeTab === type.key ? '#FFFFFF' : '#374151', // Ensure visible text color
                        }}
                      >
                        <Icon className="w-5 h-5" />
                        <span>{type.label}</span>
                      </button>
                    )
                  })}
                </nav>
              </Card.Body>
            </Card>

            {/* 统计卡片 */}
            <Card className="mt-4">
              <Card.Body>
                <div className="text-center">
                  <div className="text-fluid-3xl font-bold text-primary mb-1">{unreadCount}</div>
                  <div className="text-sm text-gray-500">条未读消息</div>
                </div>
              </Card.Body>
            </Card>
          </div>

          {/* 右侧内容 */}
          <div className="lg:col-span-3">
            {selectedNotification ? (
              /* 通知详情 */
              <Card>
                <Card.Header>
                  <div className="flex items-center justify-between">
                    <button 
                      className="text-primary hover:underline"
                      onClick={() => setSelectedNotification(null)}
                    >
                      ← 返回列表
                    </button>
                  </div>
                </Card.Header>
                <Card.Body>
                  <div className="flex items-start gap-4 mb-6">
                    {(() => {
                      const config = typeConfig[selectedNotification.notification_type] || typeConfig.announcement
                      const Icon = config.icon
                      return (
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${config.color}`}>
                          <Icon className="w-6 h-6" />
                        </div>
                      )
                    })()}
                    <div className="flex-1">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs mb-2 ${typeConfig[selectedNotification.notification_type]?.color || 'bg-gray-100'}`}>
                        {typeConfig[selectedNotification.notification_type]?.label || '通知'}
                      </span>
                      <h3 className="text-xl font-semibold">{selectedNotification.title}</h3>
                      <p className="text-sm text-gray-500 mt-1">
                        {formatDateTime(selectedNotification.create_time, 'YYYY-MM-DD HH:mm:ss')}
                      </p>
                    </div>
                  </div>
                  <div className="prose max-w-none">
                    <p className="text-gray-700 whitespace-pre-wrap">{selectedNotification.content}</p>
                  </div>
                  {selectedNotification.link_url && (
                    <div className="mt-6 pt-6 border-t">
                      <Button href={selectedNotification.link_url}>
                        查看详情
                      </Button>
                    </div>
                  )}
                </Card.Body>
              </Card>
            ) : (
              /* 通知列表 */
              <Card>
                <Card.Header>
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold">
                      {notificationTypes.find(t => t.key === activeTab)?.label || '全部消息'}
                    </h4>
                    <span className="text-sm text-gray-500">
                      共 {notifications.length} 条
                    </span>
                  </div>
                </Card.Header>
                <Card.Body className="p-0">
                  {isLoading ? (
                    <div className="text-center py-20">
                      <LoadingSpinner size="lg" />
                    </div>
                  ) : notifications.length === 0 ? (
                    <div className="text-center py-12">
                      <FaBell className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500">暂无消息</p>
                    </div>
                  ) : (
                    <div className="divide-y">
                      {notifications.map((notification) => {
                        const config = typeConfig[notification.notification_type] || typeConfig.announcement
                        const Icon = config.icon
                        return (
                          <div
                            key={notification.id}
                            className={`flex items-start gap-4 p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                              !notification.is_read ? 'bg-blue-50/50' : ''
                            }`}
                            onClick={() => handleNotificationClick(notification)}
                          >
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${config.color}`}>
                              <Icon className="w-5 h-5" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                {!notification.is_read && (
                                  <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                                )}
                                <h5 className={`font-medium truncate ${!notification.is_read ? 'text-gray-900' : 'text-gray-700'}`}>
                                  {notification.title}
                                </h5>
                              </div>
                              <p className="text-sm text-gray-500 line-clamp-2">{notification.content}</p>
                              <p className="text-xs text-gray-400 mt-2">
                                {fromNow(notification.create_time)}
                              </p>
                            </div>
                            <div className="flex-shrink-0">
                              {notification.is_read ? (
                                <FaEnvelopeOpen className="w-4 h-4 text-gray-400" />
                              ) : (
                                <FaEnvelope className="w-4 h-4 text-primary" />
                              )}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </Card.Body>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
